import React, { useCallback, useEffect, useState } from 'react';
import './AccountingReports.css';
import HomeButton from './HomeButton';
import api from '../services/api';

const CHAT_API_BASE = '/api/chat';

async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } });
}

const fmtCell = (value, format) => {
  if (value === null || value === undefined || value === '') return '';
  if (format === 'currency') return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(value) || 0);
  if (format === 'number') return new Intl.NumberFormat('en-US').format(parseFloat(value) || 0);
  if (format === 'percent') return `${(parseFloat(value) || 0).toFixed(1)}%`;
  if (format === 'datetime') {
    const n = Number(value);
    return n ? new Date(n * 1000).toLocaleString() : '';  // epoch seconds -> viewer's local time
  }
  return String(value);
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const yearStartISO = () => `${new Date().getFullYear()}-01-01`;
const monthStartISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };

const csvEscape = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

const AccountingReports = ({ onBack }) => {
  const [definitions, setDefinitions] = useState([]);
  const [reportType, setReportType] = useState('');
  const [params, setParams] = useState({});
  const [result, setResult] = useState(null);     // { title, columns, rows, summary }
  const [viewingSaved, setViewingSaved] = useState(null); // saved report id when viewing a snapshot
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState([]);
  const [timeIsAdmin, setTimeIsAdmin] = useState(false);
  const [timeUsers, setTimeUsers] = useState([]);
  const [timeProjects, setTimeProjects] = useState([]);

  const currentUserId = (() => {
    try { return (JSON.parse(sessionStorage.getItem('user') || 'null') || {}).id || ''; }
    catch (e) { return ''; }
  })();

  const currentDef = definitions.find(d => d.key === reportType);

  // Seed default params for a report. Dates get sensible defaults; Project Time
  // starts "From" at the first of the current month.
  const seedParamsFor = (def) => {
    const seed = {};
    (def?.params || []).forEach(p => {
      if (p.type === 'date') {
        seed[p.key] = p.key === 'from'
          ? (def.key === 'project_time' ? monthStartISO() : yearStartISO())
          : todayISO();
      } else seed[p.key] = '';
    });
    return seed;
  };

  // Build query string for the active report + params.
  const reportQuery = useCallback(() => {
    const qs = new URLSearchParams({ report: reportType });
    Object.entries(params).forEach(([k, v]) => { if (v !== '' && v != null) qs.set(k, v); });
    if (currentUserId) qs.set('acting_user_id', currentUserId);  // scope enforcement (see backend)
    return qs.toString();
  }, [reportType, params, currentUserId]);

  const fetchSaved = useCallback(async () => {
    try {
      const res = await authFetch(`${CHAT_API_BASE}/accounting-reports.php?action=list_saved`);
      const data = await res.json();
      if (data.success) setSaved(data.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  // Load report definitions + saved list on mount.
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${CHAT_API_BASE}/accounting-reports.php?action=definitions`);
        const data = await res.json();
        if (data.success && data.data.length) {
          setDefinitions(data.data);
          const first = data.data[0];
          setReportType(first.key);
          setParams(seedParamsFor(first));
        }
      } catch (e) { setErr('Failed to load report types'); }
    })();
    fetchSaved();
  }, [fetchSaved]);

  // Project Time report: load the admin-only user filter. Non-admins get
  // is_admin=false (no dropdown) and the backend scopes them to their own time.
  useEffect(() => {
    if (reportType !== 'project_time') { setTimeIsAdmin(false); setTimeUsers([]); setTimeProjects([]); return; }
    (async () => {
      try {
        const res = await authFetch(`${CHAT_API_BASE}/accounting-reports.php?action=report_users&acting_user_id=${encodeURIComponent(currentUserId)}`);
        const data = await res.json();
        if (data.success) {
          setTimeIsAdmin(!!data.data.is_admin);
          setTimeUsers(data.data.users || []);
          setTimeProjects(data.data.projects || []);
        } else { setTimeIsAdmin(false); setTimeUsers([]); setTimeProjects([]); }
      } catch (e) { setTimeIsAdmin(false); setTimeUsers([]); setTimeProjects([]); }
    })();
  }, [reportType, currentUserId]);

  // Re-seed params when switching report type.
  const handleReportChange = (key) => {
    setReportType(key);
    setViewingSaved(null);
    setResult(null);
    const def = definitions.find(d => d.key === key);
    setParams(seedParamsFor(def));
  };

  const runReport = async () => {
    setRunning(true); setErr(null); setViewingSaved(null);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/accounting-reports.php?${reportQuery()}`);
      const data = await res.json();
      if (data.success) setResult(data.data.result);
      else setErr(data.error || 'Failed to run report');
    } catch (e) { setErr('Failed to run report'); }
    setRunning(false);
  };

  const saveReport = async () => {
    if (!result) return;
    const name = window.prompt('Name this saved report:', result.title || currentDef?.label || 'Report');
    if (name === null) return;
    setSaving(true); setErr(null);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/accounting-reports.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ report: reportType, params, name }),
      });
      const data = await res.json();
      if (data.success) { await fetchSaved(); }
      else setErr(data.error || 'Save failed');
    } catch (e) { setErr('Save failed'); }
    setSaving(false);
  };

  // PDF endpoint requires Bearer auth, so fetch as a blob (a plain <a href> would 401).
  const downloadPdfUrl = async (url) => {
    const res = await authFetch(url);
    if (!res.ok) throw new Error('PDF generation failed');
    const blob = await res.blob();
    const dl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dl;
    a.download = 'Accounting_Report.pdf';
    a.click();
    window.URL.revokeObjectURL(dl);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const url = viewingSaved
        ? `${CHAT_API_BASE}/accounting-report-pdf.php?saved_id=${viewingSaved}`
        : `${CHAT_API_BASE}/accounting-report-pdf.php?${reportQuery()}`;
      await downloadPdfUrl(url);
    } catch (e) { alert('Failed to generate PDF: ' + e.message); }
    setDownloading(false);
  };

  // CSV is built client-side from the rendered result (columns + rows), so the
  // values match the on-screen report (dates in local time, etc.).
  const downloadCsv = () => {
    if (!result) return;
    const cols = result.columns || [];
    const lines = [cols.map(c => csvEscape(c.label)).join(',')];
    (result.rows || []).forEach(row => {
      if (row._style === 'group') {
        // State group header: name in the first column, rest blank.
        lines.push([csvEscape(row.category), ...cols.slice(1).map(() => '')].join(','));
      } else {
        lines.push(cols.map(c => csvEscape(fmtCell(row[c.key], c.format))).join(','));
      }
    });
    if (result.summary?.length) {
      lines.push('');
      result.summary.forEach(s => lines.push(csvEscape(s.label) + ',' + csvEscape(fmtCell(s.value, s.format))));
    }
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const dl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dl;
    a.download = ((result.title || 'report').replace(/[^\w.-]+/g, '_')) + '.csv';
    a.click();
    window.URL.revokeObjectURL(dl);
  };

  const downloadSavedPdf = async (id) => {
    try { await downloadPdfUrl(`${CHAT_API_BASE}/accounting-report-pdf.php?saved_id=${id}`); }
    catch (e) { alert('Failed to generate PDF: ' + e.message); }
  };

  const openSaved = async (id) => {
    setErr(null);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/accounting-reports.php?action=get_saved&id=${id}`);
      const data = await res.json();
      if (data.success) {
        setResult(data.data.result);
        setReportType(data.data.report_type);
        setParams(data.data.params || {});
        setViewingSaved(id);
      }
    } catch (e) { setErr('Failed to open saved report'); }
  };

  const deleteSaved = async (id) => {
    if (!window.confirm('Delete this saved report?')) return;
    try {
      await authFetch(`${CHAT_API_BASE}/accounting-reports.php?id=${id}`, { method: 'DELETE' });
      if (viewingSaved === id) { setViewingSaved(null); setResult(null); }
      fetchSaved();
    } catch (e) { /* ignore */ }
  };

  // KPI icons keyed by summary label; falls back to a generic dot for
  // labels the report engine hasn't seen before.
  const kpiIconFor = (label) => {
    const key = String(label || '').toLowerCase();
    if (key.includes('project')) return '📊';
    if (key.includes('revenue') || key.includes('income') || key.includes('collected')) return '💰';
    if (key.includes('cost') || key.includes('expense') || key.includes('paid')) return '📄';
    if (key.includes('owed') || key.includes('due')) return '⚖️';
    if (key.includes('profit') || key.includes('margin') || key.includes('net')) return '📈';
    return '•';
  };

  // "Profitability by Project · Jan 01–Sep 02, 2026" — appends the from→to
  // range to the backend title when both dates are present.
  const formatRange = () => {
    const from = params?.from;
    const to = params?.to;
    if (!from || !to) return '';
    const fmt = (iso) => {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    };
    return `${fmt(from)} – ${fmt(to)}`;
  };
  // Only append the pretty date range when the backend title doesn't already
  // include ISO dates (some reports embed "2026-01-01 to 2026-09-13" already).
  const titleAlreadyHasRange = result && /\d{4}-\d{2}-\d{2}/.test(result.title || '');
  const resultTitle = result
    ? (formatRange() && !titleAlreadyHasRange ? `${result.title} · ${formatRange()}` : result.title)
    : '';

  const kpis = (result?.summary || []).slice(0, 4);

  return (
    <div className="acct-reports">
      <div className="acct-header">
        <HomeButton onClick={onBack} />
        <button className="back-button" onClick={onBack}>← Back</button>
        <div className="acct-header-title">
          <h1 className="acct-title">Accounting Reports</h1>
          <p className="acct-subtitle">Understand project performance and studio profitability.</p>
        </div>
      </div>

      <div className="acct-body">
        {/* Controls — inline row: filters on the left, actions on the right */}
        <div className="acct-controls">
          <div className="acct-controls-left">
            <div className="acct-field">
              <label>Report</label>
              <select value={reportType} onChange={e => handleReportChange(e.target.value)}>
                {definitions.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </div>
            {(currentDef?.params || []).map(p => (
              <div className="acct-field" key={p.key}>
                <label>{p.label}</label>
                <input
                  type={p.type === 'date' ? 'date' : 'text'}
                  value={params[p.key] || ''}
                  onChange={e => setParams(prev => ({ ...prev, [p.key]: e.target.value }))}
                />
              </div>
            ))}
            {reportType === 'project_time' && (
              <div className="acct-field">
                <label>Project</label>
                <select value={params.project_id || ''} onChange={e => setParams(prev => ({ ...prev, project_id: e.target.value }))}>
                  <option value="">All projects</option>
                  {timeProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            {reportType === 'project_time' && timeIsAdmin && (
              <div className="acct-field">
                <label>User</label>
                <select value={params.user_id || ''} onChange={e => setParams(prev => ({ ...prev, user_id: e.target.value }))}>
                  <option value="">All users</option>
                  {timeUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="acct-actions">
            <button className="acct-btn acct-btn-primary" onClick={runReport} disabled={running || !reportType}>
              {running ? 'Running…' : 'Run Report'}
            </button>
            <button className="acct-btn" onClick={saveReport} disabled={!result || saving || viewingSaved}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="acct-btn" onClick={downloadPdf} disabled={!result || downloading}
                    title="Download PDF">
              {downloading ? 'Preparing…' : '⬇ Download PDF'}
            </button>
            <button className="acct-btn acct-btn-ghost" onClick={downloadCsv} disabled={!result}
                    title="Download CSV">CSV</button>
          </div>
        </div>
        {currentDef?.description && <p className="acct-desc">{currentDef.description}</p>}
        {err && <p className="acct-err">{err}</p>}

        {/* KPI row — one card per summary metric, up to 4 */}
        {kpis.length > 0 && (
          <div className="acct-kpis">
            {kpis.map((s, i) => (
              <div className="acct-kpi" key={i}>
                <div className="acct-kpi-icon" aria-hidden="true">{kpiIconFor(s.label)}</div>
                <div className="acct-kpi-body">
                  <div className="acct-kpi-label">{s.label}</div>
                  <div className="acct-kpi-value">{fmtCell(s.value, s.format)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="acct-main">
          {/* Result */}
          <div className="acct-result">
            {viewingSaved && <div className="acct-snapshot-banner">📌 Viewing a saved snapshot (frozen at save time)</div>}
            {!result ? (
              <div className="acct-empty-state">
                <div className="acct-empty-illustration" aria-hidden="true">📊</div>
                <h3 className="acct-empty-title">Run a report to see results</h3>
                <p className="acct-empty-body">Pick a report type and date range above, then click <strong>Run Report</strong>.</p>
              </div>
            ) : (
              <>
                <h2 className="acct-result-title">{resultTitle}</h2>
                <div className="acct-table-wrap">
                  <table className="acct-table">
                    <caption className="visually-hidden">{resultTitle}</caption>
                    <thead>
                      <tr>{result.columns.map(c => (
                        <th key={c.key} scope="col" className={c.align === 'right' ? 'r' : ''}>{c.label}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {result.rows.length === 0 ? (
                        <tr>
                          <td colSpan={result.columns.length} className="acct-no-data">
                            <div className="acct-empty-illustration" aria-hidden="true">📊</div>
                            <div className="acct-empty-title">No data for this period</div>
                            <div className="acct-empty-body">Adjust the date range or run another report.</div>
                          </td>
                        </tr>
                      ) : result.rows.map((row, i) => (
                        row._style === 'group' ? (
                          <tr key={i} className="acct-group-row">
                            <td colSpan={result.columns.length}>{row.category}</td>
                          </tr>
                        ) : (
                          <tr key={i} className={row._style === 'subtotal' ? 'acct-subtotal-row' : row._style === 'total' ? 'acct-total-row' : ''}>
                            {result.columns.map(c => (
                              <td key={c.key} className={c.align === 'right' ? 'r' : ''}>{fmtCell(row[c.key], c.format)}</td>
                            ))}
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Saved reports */}
          <aside className="acct-saved">
            <h3>Saved Reports</h3>
            {saved.length === 0 ? (
              <div className="acct-saved-empty">
                <div className="acct-saved-empty-icon" aria-hidden="true">🔖</div>
                <p className="acct-saved-empty-title">No saved reports yet.</p>
                <p className="acct-saved-empty-body">Save a report to quickly access it here.</p>
              </div>
            ) : saved.map(s => (
              <div className={`acct-saved-item ${viewingSaved === s.id ? 'active' : ''}`} key={s.id}>
                <button className="acct-saved-open" onClick={() => openSaved(s.id)} title="Open snapshot">
                  <span className="acct-saved-name">{s.name}</span>
                  <span className="acct-saved-date">{(s.generated_at || '').slice(0, 10)}</span>
                </button>
                <div className="acct-saved-actions">
                  <button className="acct-saved-pdf" onClick={() => downloadSavedPdf(s.id)} title="Download PDF">PDF</button>
                  <button className="acct-saved-del" onClick={() => deleteSaved(s.id)} title="Delete">×</button>
                </div>
              </div>
            ))}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AccountingReports;
